import { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext.jsx';
import { useFontBinary } from '../../hooks/useFontBinary.js';

export default function GlyphMapTab({ font: fontData }) {
  const { theme } = useTheme();
  const fileId = fontData.files?.[0]?.id;
  const { font: otFont, loading, error } = useFontBinary(fontData.id, fileId);
  const [glyphs, setGlyphs] = useState([]);
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (!otFont) return;
    const chars = [];
    for (let i = 32; i < 0xFFFF; i++) {
      const glyph = otFont.charToGlyph(String.fromCodePoint(i));
      if (glyph && glyph.index > 0) {
        chars.push({
          char: String.fromCodePoint(i),
          code: i,
          name: glyph.name || `U+${i.toString(16).toUpperCase().padStart(4, '0')}`,
        });
      }
      if (chars.length >= 2000) break; // cap for performance
    }
    setGlyphs(chars);
  }, [otFont]);

  const filtered = search
    ? glyphs.filter(g =>
        g.char.toLowerCase().includes(search.toLowerCase()) ||
        g.name.toLowerCase().includes(search.toLowerCase()) ||
        g.code.toString(16).includes(search.toLowerCase())
      )
    : glyphs;

  const handleCopy = (char) => {
    navigator.clipboard.writeText(char);
    setCopied(char);
    setTimeout(() => setCopied(null), 1000);
  };

  if (loading) return <div style={{ color: theme.textMuted, fontSize: 12, padding: 20 }}>Loading glyphs...</div>;
  if (error) return <div style={{ color: theme.red, fontSize: 12, padding: 20 }}>Error: {error}</div>;
  if (!otFont) return <div style={{ color: theme.textMuted, fontSize: 12, padding: 20 }}>No font file available</div>;

  return (
    <div>
      <input
        type="text"
        placeholder="Search by character, name, or hex code..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{
          width: '100%', padding: '8px 10px', marginBottom: 12,
          background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
          borderRadius: 6, color: theme.text, fontSize: 12, outline: 'none',
          fontFamily: 'inherit',
        }}
      />
      <div style={{ fontSize: 10, color: theme.textGhost, marginBottom: 8 }}>
        {filtered.length} glyphs {search && `(filtered from ${glyphs.length})`}
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(42px, 1fr))',
        gap: 4, maxHeight: 400, overflowY: 'auto',
      }}>
        {filtered.slice(0, 500).map(g => (
          <button
            key={g.code}
            onClick={() => handleCopy(g.char)}
            title={`${g.name}\nU+${g.code.toString(16).toUpperCase().padStart(4, '0')}\nClick to copy`}
            style={{
              width: '100%', aspectRatio: '1', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              fontFamily: `'${fontData.family}', serif`,
              fontSize: 18, color: theme.text,
              background: copied === g.char ? theme.accentBg : theme.bgTertiary,
              border: `1px solid ${copied === g.char ? theme.accentBorder : theme.borderLight}`,
              borderRadius: 4, cursor: 'pointer',
              transition: 'all 0.1s',
            }}
          >{g.char}</button>
        ))}
      </div>
      {filtered.length > 500 && (
        <div style={{ fontSize: 10, color: theme.textGhost, marginTop: 8, textAlign: 'center' }}>
          Showing first 500 of {filtered.length} glyphs
        </div>
      )}
    </div>
  );
}
